import React, { useEffect, useState } from 'react';
import { useField } from 'formik';
import styled from 'styled-components';

import { get } from '../../../lib/api';
import { useDidUpdate } from '../../../lib/customHooks';
import CrossIcon from '../../Images/CrossIcon';
import DownArrow from '../../Images/DownArrow';

const Container = styled.div``;

const DropDownHolder = styled.div`
  position: relative;
`;
const OptionsDropDown = styled.div`
  width: 320px;
  max-height: 352px;
  overflow-y: auto;
  box-shadow: 0 20px 40px 0 rgba(24, 27, 37, 0.16);
  background-color: var(--Dove);
  position: ${({ notAbsolutePosition }) => !notAbsolutePosition && 'absolute'};
  top: 0;
  left: 0;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  z-index: 1000;
`;
const Option = styled.div`
  background-color: var(--Dove);
  padding: 16px;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  color: var(--Text500);

  &:hover {
    background-color: var(--Grey500);
  }
`;

const BoldSpan = styled.span`
  color: var(--Text900);
`;

const Label = styled.label`
  margin-right: 6px;
`;
const LabelDiv = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  top: 6px;
  line-height: 1.33;
  text-align: left;
  color: var(--Text300);
  position: absolute;
`;

const SelectionField = styled.div`
  position: relative;
  min-height: 50px;
  display: flex;
  flex-wrap: wrap;
  border-radius: 4px;
  border: solid 1px var(--Grey900);
  background-color: var(--Grey300);
  // padding: 6px 8px;
  padding: 24px 16px 14px 16px;

  &:focus-within {
    background-color: var(--Dove);
    border: solid 2px var(--Azure500);
    // padding: 5px 7px;
    padding: 23px 7px 13px 7px;
  }
`;

const SelectedValue = styled.div`
  border-radius: 4px;
  background-color: var(--Fjord800);
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.43;
  color: var(--TextDark700);
  padding-right: 6px;
  margin-right: 8px;
  margin-top: 3px;
`;

const SelectedSpan = styled.span`
  display: flex;
  align-items: center;
  width: 100%;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  color: var(--Text700);
  justify-content: space-between;
`;

const Input = styled.input`
  flex-grow: 1;
  border: none;
  background-color: var(--Grey300);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  color: var(--Text700);
  margin-top: 3px;
  min-height: 36px;
  cursor: ${({ readOnly }) => (readOnly ? 'not-allowed' : 'auto')};
  &:focus {
    outline: none;
  }

  &:focus {
    background-color: var(--Dove);
  }
`;

const StyledDownArrow = styled(DownArrow)`
  align-self: center;
`;
const StyledCrossIcon = styled(CrossIcon)`
  align-self: center;
  fill: #0d1536;
  fill-opacity: 0.65;
`;

const SearchableSingleSelectDropdown = ({
  label,
  placeholder,
  nameKey,
  selectByName,
  selectKey,
  apiUrl,
  valueArray,
  notAbsolutePosition,
  isMandatory,
  ...props
}) => {
  const [isOpen, toggleDropDownOpen] = useState(false);
  const [field, meta] = useField(props);
  const { value = '', onBlur, onChange } = field;
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);

  const EnterAgain = () => {
    setInputValue('');
    onChange({ target: { name: props.name, value: '' } });
    toggleDropDownOpen(!isOpen);
  };

  const newHandleChange = (name) => {
    // do something with this val
    toggleDropDownOpen(false);
    onChange({ target: { name: props.name, value: name } });
  };

  const handleInputChange = (e) => {
    toggleDropDownOpen(true);
    setInputValue(e.target.value);
  };

  const handleClick = () => {
    toggleDropDownOpen(false);
    setInputValue('');
  };

  useEffect(() => {
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  useDidUpdate(() => {
    onChange({ target: { name: props.name, value: '' } });
    setOptions([]);
  }, [apiUrl]);

  useEffect(() => {
    if (!apiUrl) {
      valueArray && setOptions(valueArray);
      return;
    }
    get(apiUrl, { params: { is_novel: 1 } }).then((response) => {
      setOptions(response.result.tags || response.result || []);
    });
  }, [apiUrl]);

  const getOptions = () => {
    const currentValues =
      value
        ?.split(',')
        ?.map((each) => each.trim())
        ?.filter(Boolean) || '';
    return options
      .filter((option) => currentValues.indexOf(option[selectKey]) === -1)
      .map((tag) => {
        if (inputValue === '') return tag;
        const index = tag[nameKey]
          .toLowerCase()
          .indexOf(inputValue.toLowerCase());
        if (index > -1) {
          const strArr = tag[nameKey].split('');
          if (index === strArr.length - 1) return tag;
          const firstArr = strArr.slice(0, index);
          const secondArr = strArr.slice(index, index + inputValue.length);
          const thirdArr = strArr.slice(index + inputValue.length);
          const substr = [
            firstArr.join(''),
            secondArr.join(''),
            thirdArr.join(''),
          ];
          return { ...tag, substr };
        } else {
          return null;
        }
      })
      .filter(Boolean);
  };

  return (
    <Container>
      <SelectionField>
        <LabelDiv>
          <Label>{label || ''}</Label>
          {isMandatory && <span className="asterisk-sign">*</span>}
        </LabelDiv>

        {value ? (
          <>
            <SelectedSpan>
              {value
                ? selectByName
                  ? value
                  : (options.find((option) => option[selectKey] === value) ||
                      {})[nameKey] || ''
                : placeholder || 'Select from options'}
              <StyledCrossIcon
                onClick={() => !props.readOnly && EnterAgain('')}
              />
            </SelectedSpan>
          </>
        ) : (
          <Input
            onChange={(e) => handleInputChange(e)}
            value={inputValue}
            placeholder={placeholder}
            {...props}
          />
        )}
      </SelectionField>
      <DropDownHolder>
        <OptionsDropDown
          isOpen={isOpen}
          notAbsolutePosition={notAbsolutePosition}
        >
          {getOptions().map((obj) => (
            <Option
              key={obj[nameKey]}
              onClick={() => newHandleChange(obj[selectKey])}
            >
              {!obj.substr && obj[nameKey]}
              {obj.substr &&
                obj.substr.map((str, i) => {
                  if (i === 1) return <BoldSpan key={i}>{str}</BoldSpan>;
                  return str;
                })}
            </Option>
          ))}
        </OptionsDropDown>
      </DropDownHolder>
    </Container>
  );
};

export default SearchableSingleSelectDropdown;
